import { useEffect, useState } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import Logo from "@/components/zeblan/Logo";

const links = [
  { href: "#our-story", label: "Our Story" },
  { href: "#services", label: "Services" },
  { href: "#how-we-work", label: "How We Work" },
  { href: "#use-cases", label: "Use Cases" },
  { href: "#pricing", label: "Pricing" },
];

export default function Navbar({ onOpenLead }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const linkBase = scrolled ? "text-navy hover:text-blue" : "text-white/85 hover:text-white";

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          scrolled ? "bg-white/85 backdrop-blur-md shadow-[0_1px_0_0_rgba(10,19,40,0.06)]" : "bg-transparent"
        }`}
        data-testid="site-navbar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[88px] flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
            <Logo className="h-14 lg:h-16 w-auto" />
          </a>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className={`text-[13px] font-medium tracking-wide transition-colors ${linkBase}`}
                data-testid={`nav-link-${l.href.replace("#", "")}`}
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={() => onOpenLead("roi-assessment")}
              className="inline-flex items-center gap-2 bg-blue hover:bg-blue-deep text-white text-[13px] font-semibold tracking-wide px-5 py-2.5 rounded-md transition-all hover:-translate-y-[1px] shadow-[0_6px_18px_-6px_rgba(37,99,235,0.55)]"
              data-testid="nav-roi-button"
            >
              Free ROI Assessment <ArrowRight className="h-4 w-4" />
            </button>
          </nav>

          <button
            className={`lg:hidden p-2 ${scrolled ? "text-navy" : "text-white"}`}
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-mobile-open"
          >
            <Menu className="h-7 w-7" />
          </button>
        </div>
      </header>

      {/* Mobile drawer rendered OUTSIDE the header so it overlays everything */}
      <div
        className={`lg:hidden fixed inset-0 z-[60] transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!open}
        data-testid="nav-mobile-drawer-root"
      >
        <div
          className="absolute inset-0 bg-navy"
          onClick={() => setOpen(false)}
        />
        <aside
          className={`absolute top-0 right-0 h-full w-full sm:w-[88%] sm:max-w-[380px] bg-navy text-white flex flex-col transition-transform duration-300 ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
          data-testid="nav-mobile-drawer"
        >
          <div className="flex items-center justify-between px-6 h-[88px] border-b border-white/10">
            <Logo className="h-12 w-auto" />
            <button
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="p-2 -mr-2 text-white/80 hover:text-white"
              data-testid="nav-mobile-close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 flex flex-col gap-1 px-4 py-6">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="block text-[18px] font-medium text-white/90 hover:text-teal hover:bg-white/[0.04] rounded-lg px-3 py-3 transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="px-6 pb-8 pt-4 border-t border-white/10">
            <button
              onClick={() => {
                setOpen(false);
                onOpenLead("roi-assessment");
              }}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue hover:bg-blue-deep text-white font-semibold px-5 py-3.5 rounded-md"
              data-testid="nav-mobile-roi-button"
            >
              Free ROI Assessment <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </aside>
      </div>
    </>
  );
}
